import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export const useClientProfile = () => {
  const { user, isAuthenticated } = useAuth();
  const [clientProfile, setClientProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchClientProfile = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setClientProfile(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiBase}/api/users/profile`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData.clientProfile) {
          console.log('✅ Client profile fetched:', userData.clientProfile);
          setClientProfile(userData.clientProfile);
        } else {
          console.log('ℹ️ No client profile found - user may not have one yet');
          setClientProfile(null);
        }
      } else if (response.status === 404) {
        // Client profile doesn't exist yet - this is normal for new users
        console.log('ℹ️ No client profile found - user may be new');
        setClientProfile(null);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch client profile');
      }
    } catch (err) {
      console.error('❌ Error fetching client profile:', err);
      setError(err.message);
      setClientProfile(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const updateClientProfile = useCallback(async (updates) => {
    if (!isAuthenticated || !user) {
      throw new Error('User must be authenticated to update client profile');
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiBase}/api/users/client-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const updatedProfile = await response.json();
        console.log('✅ Client profile updated:', updatedProfile);
        setClientProfile(updatedProfile);
        return updatedProfile;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update client profile');
      }
    } catch (err) {
      console.error('❌ Error updating client profile:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Fetch client profile when user authenticates
  useEffect(() => {
    fetchClientProfile();
  }, [fetchClientProfile]);

  // Helper function to get scheduling preferences
  const getSchedulingPreferences = useCallback(() => {
    if (!clientProfile?.preferences) return null;
    
    return {
      schedulingFrequency: clientProfile.preferences.schedulingFrequency,
      preferredServiceTime: clientProfile.preferences.preferredServiceTime,
      flexibleDates: clientProfile.preferences.flexibleDates || false
    };
  }, [clientProfile]);

  // Helper function to get saved address
  const getSavedAddress = useCallback(() => {
    if (!clientProfile) return null;
    if (!clientProfile.address) return null;
    return {
      address: clientProfile.address,
      city: clientProfile.city,
      state: clientProfile.state,
      zipCode: clientProfile.zipCode
    };
  }, [clientProfile]);

  // Helper function to get required services
  const getRequiredServices = useCallback(() => {
    return clientProfile?.requiredServices || [];
  }, [clientProfile]);

  // Helper function to check if this is a returning customer
  const isReturningCustomer = useCallback(() => {
    return clientProfile && (
      clientProfile.requiredServices?.length > 0 ||
      clientProfile.address ||
      clientProfile.preferences
    );
  }, [clientProfile]);

  return {
    clientProfile,
    loading,
    error,
    fetchClientProfile,
    updateClientProfile,
    
    // Helper functions
    getSchedulingPreferences,
    getSavedAddress,
    getRequiredServices,
    isReturningCustomer,
    
    // Computed properties
    hasProfile: !!clientProfile,
    hasAddress: !!(clientProfile?.address),
    hasPreferences: !!(clientProfile?.preferences),
    hasRequiredServices: !!(clientProfile?.requiredServices?.length > 0)
  };
};

export default useClientProfile;